<template>
    <div class="manuscript-list">
        <ul class="manuscript-list__items">
            <li v-for="manuscript in manuscripts" :key="manuscript.manuscriptKey" class="manuscript-list__item"
                :class="{ 'manuscript-list__item--active': manuscript.manuscriptKey === activeManuscriptKey }">
                <button type="button" class="manuscript-list__item-button"
                    @click="onClickManuscript(manuscript.manuscriptKey)">
                    <span class="manuscript-list__item-title">{{ manuscript.title }}</span>
                    <span v-if="(manuscript.issueCount ?? 0) > 0" class="manuscript-list__item-badge">
                        {{ manuscript.issueCount }}
                    </span>
                </button>
            </li>
        </ul>
    </div>
</template>
<script lang="ts" setup>
import { defineEmits, defineProps } from 'vue';

export type ManuscriptListItem = {
    manuscriptKey: string;
    title: string;
    issueCount?: number;
};

defineProps<{
    manuscripts: ManuscriptListItem[];
    activeManuscriptKey?: string;
}>();

const emit = defineEmits<{
    (event: 'manuscript-click', manuscriptKey: string): void;
}>();

function onClickManuscript(manuscriptKey: string) {
    emit('manuscript-click', manuscriptKey);
}
</script>
<style lang="scss" scoped>
@import "@/style-common.scss";

.manuscript-list {
    flex: 0 0 240px;
    min-height: 0;
    overflow-y: auto;
    padding: 12px;
    background-color: var(--color-background-normal);
    border-right: 1px solid var(--color-border);

    &__items {
        margin: 0;
        padding: 0;
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    &__item {
        &--active .manuscript-list__item-button {
            background-color: var(--color-surface-muted);
        }
    }

    &__item-button {
        position: relative;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        width: 100%;
        padding: 10px 14px;
        border: none;
        border-radius: var(--radius-sm);
        background-color: transparent;
        color: var(--color-text-normal);
        text-align: left;
        cursor: pointer;

        @include hover-overlay;
    }

    &__item-title {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-bold);
        line-height: 1.4;
    }

    &__item-badge {
        flex: 0 0 auto;
        min-width: 20px;
        padding: 1px 6px;
        border-radius: var(--radius-xs);
        background-color: var(--color-danger);
        color: var(--color-text-on-fixed-bg);
        font-size: var(--font-size-xs);
        font-weight: var(--font-weight-bold);
        text-align: center;
        line-height: 1.5;
    }
}
</style>
